import React, { useState, useEffect, useRef } from 'react';
import { api } from '../api';

const QuoteSign = () => {
  const token = window.location.pathname.split('/sign/')[1];
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [signerName, setSignerName] = useState('');
  const [signed, setSigned] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const lastPos = useRef(null);

  useEffect(() => {
    if (!token) { setError('Lien invalide'); return; }
    api.getPublicQuote(token)
      .then(d => {
        if (d.error) setError(d.error);
        else setData(d);
      })
      .catch(() => setError('Erreur lors du chargement'));
  }, [token]);

  // Canvas drawing
  const getPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const src = e.touches ? e.touches[0] : e;
    return { x: (src.clientX - rect.left) * (canvas.width / rect.width), y: (src.clientY - rect.top) * (canvas.height / rect.height) };
  };

  const startDraw = (e) => {
    e.preventDefault();
    drawing.current = true;
    const canvas = canvasRef.current;
    lastPos.current = getPos(e, canvas);
  };

  const draw = (e) => {
    e.preventDefault();
    if (!drawing.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = getPos(e, canvas);
    ctx.beginPath();
    ctx.moveTo(lastPos.current.x, lastPos.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = '#1e3a5f';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.stroke();
    lastPos.current = pos;
  };

  const stopDraw = () => { drawing.current = false; };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  const isCanvasEmpty = () => {
    const canvas = canvasRef.current;
    if (!canvas) return true;
    const ctx = canvas.getContext('2d');
    const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
    return !data.some(v => v !== 0);
  };

  const handleSign = async () => {
    if (isCanvasEmpty()) { alert('Veuillez apposer votre signature dans le cadre.'); return; }
    if (!signerName.trim()) { alert('Veuillez indiquer votre nom complet.'); return; }
    setSubmitting(true);
    try {
      const canvas = canvasRef.current;
      const signatureData = canvas.toDataURL('image/png');
      const r = await api.signQuote(token, signatureData, signerName.trim());
      if (r.error) { alert(r.error); setSubmitting(false); return; }
      setSigned(true);
    } catch (e) { alert('Erreur lors de la signature. Réessayez.'); setSubmitting(false); }
  };

  const s = {
    page: { minHeight: '100vh', background: '#f8fafc', fontFamily: 'system-ui, sans-serif', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '2rem 1rem' },
    card: { background: 'white', borderRadius: '1rem', boxShadow: '0 4px 24px rgba(0,0,0,0.08)', padding: '2rem', width: '100%', maxWidth: '600px', marginBottom: '1.5rem' },
    title: { fontSize: '1.5rem', fontWeight: '700', color: '#0f172a', margin: '0 0 0.25rem' },
    sub: { color: '#64748b', fontSize: '0.9rem', marginBottom: '1.5rem' },
    row: { display: 'flex', justifyContent: 'space-between', padding: '0.6rem 0', borderBottom: '1px solid #f1f5f9' },
    label: { color: '#64748b', fontSize: '0.9rem' },
    val: { color: '#0f172a', fontWeight: '600', fontSize: '0.9rem' },
    input: { width: '100%', border: '1px solid #e2e8f0', borderRadius: '0.5rem', padding: '0.75rem', fontSize: '0.95rem', outline: 'none', boxSizing: 'border-box', marginBottom: '1rem' },
    btn: { background: 'linear-gradient(135deg,#3b82f6,#2563eb)', color: 'white', border: 'none', borderRadius: '0.5rem', padding: '0.85rem 2rem', cursor: 'pointer', fontWeight: '700', fontSize: '1rem', width: '100%' },
    btnClear: { background: '#f1f5f9', color: '#64748b', border: '1px solid #e2e8f0', borderRadius: '0.5rem', padding: '0.5rem 1rem', cursor: 'pointer', fontSize: '0.85rem', marginBottom: '0.75rem' },
  };

  if (error) return (
    <div style={s.page}>
      <div style={{ ...s.card, textAlign: 'center' }}>
        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>❌</div>
        <h2 style={{ color: '#0f172a' }}>{error}</h2>
        <p style={{ color: '#64748b' }}>Ce lien est invalide ou a expiré.</p>
      </div>
    </div>
  );

  if (!data) return (
    <div style={s.page}>
      <div style={{ ...s.card, textAlign: 'center', color: '#64748b' }}>Chargement...</div>
    </div>
  );

  if (signed || data.alreadySigned) return (
    <div style={s.page}>
      <div style={{ ...s.card, textAlign: 'center' }}>
        <div style={{ fontSize: '3.5rem', marginBottom: '1rem' }}>✅</div>
        <h2 style={{ color: '#0f172a', marginBottom: '0.5rem' }}>Devis signé !</h2>
        <p style={{ color: '#64748b' }}>
          {data.alreadySigned && !signed
            ? `Ce devis a déjà été signé${data.signerName ? ` par ${data.signerName}` : ''}${data.signedAt ? ` le ${new Date(data.signedAt).toLocaleDateString('fr-FR')}` : ''}.`
            : `Votre signature a bien été enregistrée. ${data.artisan?.name || 'L\'artisan'} va être notifié.`}
        </p>
      </div>
    </div>
  );

  const { quote, artisan } = data;
  return (
    <div style={s.page}>
      {/* En-tête artisan */}
      <div style={{ ...s.card, background: 'linear-gradient(135deg,#1e3a5f,#2563eb)', color: 'white' }}>
        <div style={{ fontSize: '0.85rem', opacity: 0.8, marginBottom: '0.25rem' }}>Devis de</div>
        <div style={{ fontSize: '1.2rem', fontWeight: '700' }}>{artisan?.nomEntreprise || artisan?.name}</div>
        {artisan?.siret && <div style={{ fontSize: '0.8rem', opacity: 0.7 }}>SIRET {artisan.siret}</div>}
      </div>

      {/* Détail devis */}
      <div style={s.card}>
        <h1 style={s.title}>Devis {quote.number}</h1>
        <p style={s.sub}>Veuillez lire attentivement et signer pour accepter ce devis.</p>
        <div style={s.row}><span style={s.label}>Client</span><span style={s.val}>{quote.client}</span></div>
        <div style={s.row}><span style={s.label}>Montant TTC</span><span style={{ ...s.val, color: '#2563eb', fontSize: '1.1rem' }}>{Number(quote.amount).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}</span></div>
        {quote.expiryDate && <div style={s.row}><span style={s.label}>Valide jusqu'au</span><span style={s.val}>{new Date(quote.expiryDate).toLocaleDateString('fr-FR')}</span></div>}
        {quote.description && <div style={{ marginTop: '1rem', padding: '1rem', background: '#f8fafc', borderRadius: '0.5rem', color: '#374151', fontSize: '0.9rem', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>{quote.description}</div>}
      </div>

      {/* Zone de signature */}
      <div style={s.card}>
        <h2 style={{ ...s.title, fontSize: '1.1rem', marginBottom: '1.5rem' }}>🖊 Votre signature</h2>

        <label style={{ display: 'block', color: '#374151', fontWeight: '600', marginBottom: '0.4rem', fontSize: '0.9rem' }}>Nom complet du signataire *</label>
        <input style={s.input} placeholder="Prénom Nom" value={signerName} onChange={e => setSignerName(e.target.value)} />

        <label style={{ display: 'block', color: '#374151', fontWeight: '600', marginBottom: '0.4rem', fontSize: '0.9rem' }}>Signature manuscrite *</label>
        <div style={{ border: '2px dashed #e2e8f0', borderRadius: '0.5rem', overflow: 'hidden', marginBottom: '0.5rem', background: '#fafafa', touchAction: 'none' }}>
          <canvas
            ref={canvasRef}
            width={560}
            height={160}
            style={{ display: 'block', width: '100%', cursor: 'crosshair' }}
            onMouseDown={startDraw}
            onMouseMove={draw}
            onMouseUp={stopDraw}
            onMouseLeave={stopDraw}
            onTouchStart={startDraw}
            onTouchMove={draw}
            onTouchEnd={stopDraw}
          />
        </div>
        <button style={s.btnClear} onClick={clearCanvas}>↺ Effacer</button>

        <p style={{ color: '#94a3b8', fontSize: '0.8rem', marginBottom: '1.5rem', lineHeight: '1.5' }}>
          En signant, vous acceptez les conditions de ce devis. Cette signature électronique a valeur contractuelle conformément au règlement eIDAS.
        </p>

        <button style={{ ...s.btn, opacity: submitting ? 0.7 : 1 }} disabled={submitting} onClick={handleSign}>
          {submitting ? 'Enregistrement...' : '✅ Signer et accepter le devis'}
        </button>
      </div>
    </div>
  );
};

export default QuoteSign;
